import { useMemo, useState, useCallback } from "react";
import { toast } from "react-toastify";

import { useMutation, useQuery } from "@tanstack/react-query";

import { Button, Grid2, Stack } from "@mui/material";
import { IconEdit, IconTrash, IconUserPlus } from "@tabler/icons-react";

import ContainerLoading from "../components/ContainerLoading";
import VisitorRegister from "../components/VisitorRegister";
import VisitorEdit from "../components/VisitorEdit";

import { visitorsList, visitorDelete } from "../services/visitors";
import PageHeader from "../components/PageHeader";
import VirtualizedTable from "../components/VirtualizeTable";
import { MRT_ColumnDef } from "material-react-table";
import moment from "moment";
import { formatPhone } from "../helpers/format";

const Visitors = () => {
  const [open, setOpen] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [selectedVisitor, setSelectedVisitor] = useState<{
    id: string;
    name: string;
    phone?: string;
    visitDate: string | Date;
    observation?: string;
  } | null>(null);

  const {
    data,
    isLoading,
    isFetching,
    refetch,
  }: {
    data: Array<{
      id: string;
      name: string;
      phone?: string;
      visitDate: string | Date;
      observation?: string;
    }>;
    isLoading: boolean;
    isFetching: boolean;
    refetch: () => void;
  } = useQuery({
    queryKey: ["visitorsList"],
    queryFn: async () => await visitorsList({}),
    initialData: [],
  });

  const { mutate: deleteMutate, isPending } = useMutation({
    mutationFn: visitorDelete,
    onSuccess: () => {
      toast.success("Visitante excluído com sucesso");
      refetch();
    },
    onError: (e: { response: { data: { message: string } } }) => {
      toast.error(
        String(e?.response?.data?.message) || "Erro ao excluir visitante"
      );
    },
  });

  const onDelete = useCallback(
    (id: string) => {
      if (window.confirm("Deseja realmente excluir este visitante?")) {
        deleteMutate(id);
      }
    },
    [deleteMutate]
  );

  const onEdit = useCallback((visitor: (typeof data)[0]) => {
    setSelectedVisitor(visitor);
    setOpenEdit(true);
  }, []);

  const columns = useMemo<MRT_ColumnDef<(typeof data)[0]>[]>(() => {
    return [
      {
        accessorKey: "name",
        header: "Nome",
      },
      {
        accessorKey: "phone",
        header: "Telefone",
        Cell: ({ cell }) => {
          const phone = cell.getValue() as string;
          return phone ? formatPhone(phone) : "-";
        },
      },
      {
        accessorKey: "visitDate",
        header: "Data da Visita",
        Cell: ({ cell }) =>
          moment(cell.getValue() as string).format("DD/MM/YYYY"),
      },
      {
        accessorKey: "observation",
        header: "Observação",
        Cell: ({ cell }) => {
          const observation = cell.getValue() as string;
          return observation || "-";
        },
      },
      {
        id: "actions",
        header: "Ações",
        size: 100,
        Cell: ({ row }: { row: { original: (typeof data)[0] } }) => (
          <Stack direction="row" spacing={1} justifyContent="center">
            <IconEdit
              onClick={() => onEdit(row.original)}
              style={{ cursor: "pointer", color: "#FFF" }}
            />
            <IconTrash
              onClick={() => onDelete(row.original.id)}
              style={{ cursor: "pointer", color: "#f44336" }}
            />
          </Stack>
        ),
        muiTableHeadCellProps: { align: "center", sx: { textAlign: "center" } },
        muiTableBodyCellProps: { align: "center", sx: { textAlign: "center" } },
        enableSorting: false,
        enableColumnActions: false,
      },
    ];
  }, [onEdit, onDelete]);

  const loading = isLoading || isFetching || isPending;

  return (
    <ContainerLoading loading={loading}>
      <Grid2 container spacing={2} padding={2}>
        <PageHeader
          title="Visitantes"
          subtitle="Gerencie os visitantes da academia"
          options={
            <Stack
              direction={{ xs: "column", md: "row" }}
              sx={{
                alignItems: { xs: "flex-start", lg: "center" },
                justifyContent: { xs: "flex-start", lg: "flex-end" },
                gap: 2,
              }}
            >
              <Button
                variant="contained"
                color="primary"
                startIcon={<IconUserPlus />}
                onClick={() => setOpen(true)}
              >
                Cadastrar Visitante
              </Button>{" "}
            </Stack>
          }
        />
        <Grid2 size={12}>
          <VirtualizedTable
            data={data}
            columns={columns}
            fetchNextPage={() => Promise.resolve()}
            isLoading={false}
          />
        </Grid2>
        <VisitorRegister
          open={open}
          onClose={() => setOpen(false)}
          refetch={refetch}
        />
        <VisitorEdit
          open={openEdit}
          onClose={() => {
            setOpenEdit(false);
            setSelectedVisitor(null);
          }}
          refetch={refetch}
          visitor={selectedVisitor}
        />
      </Grid2>
    </ContainerLoading>
  );
};

export default Visitors;
